/**
 * Google Analytics 4 (gtag) – nur nach Einwilligung, Mess-ID per VITE_GA_MEASUREMENT_ID.
 */

const CONSENT_KEY = 'portfolio-3topf-analytics-consent';

export type AnalyticsConsentValue = 'granted' | 'denied';

export function getGaMeasurementId(): string | undefined {
  const id = import.meta.env.VITE_GA_MEASUREMENT_ID?.trim();
  return id || undefined;
}

export function isAnalyticsEnabled(): boolean {
  return Boolean(getGaMeasurementId());
}

export function getStoredAnalyticsConsent(): AnalyticsConsentValue | null {
  try {
    const v = localStorage.getItem(CONSENT_KEY);
    if (v === 'granted' || v === 'denied') {
      return v;
    }
  } catch {
    /* ignore */
  }
  return null;
}

export function setAnalyticsConsent(value: AnalyticsConsentValue): void {
  try {
    localStorage.setItem(CONSENT_KEY, value);
  } catch {
    /* ignore */
  }
}

function ensureGtagStub(): void {
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    window.gtag = function gtag(...args: unknown[]) {
      window.dataLayer.push(args);
    };
  }
}

/** Consent Mode v2: alle nicht genutzten Werbe-Signale standardmäßig verweigert. */
const CONSENT_DENIED_ALL: Record<string, string> = {
  analytics_storage: 'denied',
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
};

const CONSENT_GRANTED_ANALYTICS_ONLY: Record<string, string> = {
  analytics_storage: 'granted',
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
};

/**
 * Consent Mode v2: Default-Zustand vor dem Laden von gtag.js setzen (u. a. EU-Traffic).
 * Muss bei aktiviertem GA einmal pro Seitenaufruf vor dem Google-Tag ausgeführt werden.
 */
export function initAnalyticsConsentDefaults(): void {
  if (!isAnalyticsEnabled()) {
    return;
  }
  ensureGtagStub();
  window.gtag('consent', 'default', { ...CONSENT_DENIED_ALL });
}

/**
 * Lädt gtag.js und aktiviert Messung (nach Zustimmung).
 */
export function activateGoogleAnalytics(): void {
  const id = getGaMeasurementId();
  if (!id || document.querySelector('script[data-portfolio-3topf-ga]')) {
    return;
  }
  ensureGtagStub();

  window.gtag('consent', 'update', { ...CONSENT_GRANTED_ANALYTICS_ONLY });

  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  s.setAttribute('data-portfolio-3topf-ga', '1');
  s.onload = () => {
    if (getStoredAnalyticsConsent() !== 'granted') {
      return;
    }
    window.gtag('js', new Date());
    window.gtag('config', id, { send_page_view: false });
    emitPageView(`${window.location.pathname}${window.location.search}`);
  };
  document.head.appendChild(s);
}

function emitPageView(pathWithQuery: string): void {
  if (getStoredAnalyticsConsent() !== 'granted') {
    return;
  }
  window.gtag('event', 'page_view', {
    page_path: pathWithQuery,
    page_title: document.title,
    page_location: window.location.href,
  });
}

/**
 * SPA-Seitenwechsel an GA4 melden (nur wenn Skript geladen und Zustimmung erteilt).
 */
export function trackPageView(pathWithQuery: string): void {
  const id = getGaMeasurementId();
  if (!id || getStoredAnalyticsConsent() !== 'granted') {
    return;
  }
  ensureGtagStub();
  if (!document.querySelector('script[data-portfolio-3topf-ga]')) {
    return;
  }
  emitPageView(pathWithQuery);
}

export function recordAnalyticsConsentAndActivate(value: AnalyticsConsentValue): void {
  setAnalyticsConsent(value);
  ensureGtagStub();
  window.gtag(
    'consent',
    'update',
    value === 'granted' ? { ...CONSENT_GRANTED_ANALYTICS_ONLY } : { ...CONSENT_DENIED_ALL }
  );
  if (value === 'granted') {
    activateGoogleAnalytics();
  }
}
